import { useState, useEffect, useCallback, useRef } from 'react'
import { Vec, type Editor } from '@tldraw/tldraw'

interface ZoomModeOverlayProps {
  isActive: boolean
  isZoomOut?: boolean
  editor: Editor | null
  onExitMode: () => void
}

const DRAG_THRESHOLD = 10

const ZoomModeOverlay = ({
  isActive,
  isZoomOut = false,
  editor,
  onExitMode,
}: ZoomModeOverlayProps) => {
  const [isModifierHeld, setIsModifierHeld] = useState(false)
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null)
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null)
  const isDraggingRef = useRef(false)

  const resetDrag = useCallback(() => {
    setDragStart(null)
    setDragCurrent(null)
    isDraggingRef.current = false
  }, [])

  useEffect(() => {
    if (!isActive) {
      setIsModifierHeld(false)
      resetDrag()
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        resetDrag()
        onExitMode()
        return
      }
      if (e.ctrlKey || e.metaKey) {
        setIsModifierHeld(true)
      }
    }

    const handleKeyUp = () => {
      setIsModifierHeld(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isActive, onExitMode, resetDrag])

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isActive || !editor) return
    e.preventDefault()
    e.stopPropagation()
    setDragStart({ x: e.clientX, y: e.clientY })
    setDragCurrent({ x: e.clientX, y: e.clientY })
    isDraggingRef.current = false
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragStart) return
    e.preventDefault()
    setDragCurrent({ x: e.clientX, y: e.clientY })

    if (!isDraggingRef.current) {
      const dx = e.clientX - dragStart.x
      const dy = e.clientY - dragStart.y
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
        isDraggingRef.current = true
      }
    }
  }

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isActive || !editor || !dragStart) return
    e.preventDefault()
    e.stopPropagation()

    if (isDraggingRef.current && dragCurrent) {
      // Marquee zoom: zoom to fit the drawn rectangle
      const p1 = editor.screenToPage({
        x: Math.min(dragStart.x, dragCurrent.x),
        y: Math.min(dragStart.y, dragCurrent.y),
      })
      const p2 = editor.screenToPage({
        x: Math.max(dragStart.x, dragCurrent.x),
        y: Math.max(dragStart.y, dragCurrent.y),
      })
      editor.zoomToBounds(
        { x: p1.x, y: p1.y, w: p2.x - p1.x, h: p2.y - p1.y },
        { animation: { duration: 200 }, inset: 0 },
      )
    } else {
      // Simple click: zoom in or out centered on click point
      // zoomIn/zoomOut expect SCREEN coordinates, not page coordinates
      const screenPoint = new Vec(e.clientX, e.clientY)
      const hasModifier = e.ctrlKey || e.metaKey

      // Determine zoom direction: isZoomOut inverts the default
      const shouldZoomOut = isZoomOut ? !hasModifier : hasModifier

      const animation = { animation: { duration: 200 } }
      if (shouldZoomOut) {
        editor.zoomOut(screenPoint, animation)
      } else {
        editor.zoomIn(screenPoint, animation)
      }
    }

    resetDrag()
  }

  if (!isActive) return null

  // Compute marquee rectangle in screen-relative coords
  const marqueeRect =
    isDraggingRef.current && dragStart && dragCurrent
      ? {
          left: Math.min(dragStart.x, dragCurrent.x),
          top: Math.min(dragStart.y, dragCurrent.y),
          width: Math.abs(dragCurrent.x - dragStart.x),
          height: Math.abs(dragCurrent.y - dragStart.y),
        }
      : null

  // Determine cursor: isZoomOut inverts the default cursors
  const defaultCursor = isZoomOut ? 'zoom-out' : 'zoom-in'
  const modifierCursor = isZoomOut ? 'zoom-in' : 'zoom-out'

  const cursor = isDraggingRef.current
    ? 'crosshair'
    : isModifierHeld
      ? modifierCursor
      : defaultCursor

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        position: 'absolute',
        top: 0,
        left: '60px',
        right: 0,
        bottom: 0,
        zIndex: 10000,
        cursor,
        pointerEvents: 'all',
      }}
    >
      {marqueeRect && (
        <div
          style={{
            position: 'fixed',
            left: marqueeRect.left,
            top: marqueeRect.top,
            width: marqueeRect.width,
            height: marqueeRect.height,
            border: '2px dashed #7c3aed',
            backgroundColor: 'rgba(124, 58, 237, 0.08)',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  )
}

export default ZoomModeOverlay
